import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  IconButton, 
  Box, 
  Grid,
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  Paper,
  Switch,
  FormControlLabel,
  Slider,
  Chip,
  Divider,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import GridOnIcon from '@mui/icons-material/GridOn';
import GridOffIcon from '@mui/icons-material/GridOff';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { LineChart } from '@mui/x-charts/LineChart';
import { BarChart } from '@mui/x-charts/BarChart';
import analyticsApi from '../../../services/analyticsapi';

const YearlySalesModal = ({ open, onClose }) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [selectedYears, setSelectedYears] = useState([]);
  const [showGrid, setShowGrid] = useState(true);
  const [showTooltip, setShowTooltip] = useState(true);
  const [chartHeight, setChartHeight] = useState(400);
  const [zoomRange, setZoomRange] = useState([0, 11]); // Default to show all months
  const [selectedMonths, setSelectedMonths] = useState('all');
  const currentYear = new Date().getFullYear();
  
  // Load initial data when modal opens
  useEffect(() => {
    if (open) {
      fetchData([currentYear, currentYear-1]);
    }
  }, [open]);

  // Fetch data based on selected years
  const fetchData = async (years) => {
    try {
      setLoading(true);
      setError(null);
      const response = await analyticsApi.getYearlySales(years);
      setData(response.data);
    } catch (err) {
      console.error("Error fetching yearly sales data:", err);
      setError("Failed to load yearly sales data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Handle year selection change
  const handleYearChange = (event) => {
    const value = event.target.value;
    setSelectedYears(typeof value === 'string' ? value.split(',') : value);
  };
  
  // Apply years filter
  const handleApplyFilter = () => {
    if (selectedYears.length > 0) {
      fetchData(selectedYears);
    }
  };

  // Handle zoom range change
  const handleZoomChange = (event, newValue) => {
    setZoomRange(newValue);
  };

  // Handle preset month selections
  const handleMonthPreset = (preset) => {
    setSelectedMonths(preset);
    switch (preset) {
      case 'q1':
        setZoomRange([0, 2]); // Jan-Mar
        break;
      case 'q2':
        setZoomRange([3, 5]); // Apr-Jun
        break;
      case 'q3':
        setZoomRange([6, 8]); // Jul-Sep
        break;
      case 'q4':
        setZoomRange([9, 11]); // Oct-Dec
        break;
      case 'h1':
        setZoomRange([0, 5]); // Jan-Jun
        break;
      case 'h2':
        setZoomRange([6, 11]); // Jul-Dec
        break;
      case 'all':
      default:
        setZoomRange([0, 11]); // All months
        break;
    }
  };

  // Prepare chart data
  const renderChart = () => {
    if (!data || !data.years_data || Object.keys(data.years_data).length === 0) {
      return (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No yearly sales data available
          </Typography>
        </Box>
      );
    }

    // Prepare data for combination chart
    const years = Object.keys(data.years_data);
    
    // Apply zoom filter to data
    const filteredLabels = data.labels.slice(zoomRange[0], zoomRange[1] + 1);
    
    // Prepare net sales line series (without type specification)
    const netSalesSeries = years
      .filter(year => data.years_data[year].net_sales)
      .map((year, index) => ({
        curve: "catmullRom",
        data: data.years_data[year].net_sales.data.slice(zoomRange[0], zoomRange[1] + 1),
        label: data.years_data[year].net_sales.label,
        showMark: false,
        color: ['#4caf50', '#2196f3', '#e91e63', '#ff9800', '#9c27b0'][index % 5],
      }));

    // For purchases, we'll create a second LineChart with bar-like visualization
    // by using area fill or creating a stacked approach
    const purchaseSeries = years
      .filter(year => data.years_data[year].purchases)
      .map((year, index) => ({
        curve: "linear",
        data: data.years_data[year].purchases.data.slice(zoomRange[0], zoomRange[1] + 1),
        label: data.years_data[year].purchases.label,
        area: true, // This will create a filled area that looks like bars
        color: ['#c8e6c9', '#bbdefb', '#f8bbd9', '#ffe0b2', '#e1bee7'][index % 5],
        showMark: false,
      }));

    // Combine all series
    const allSeries = [...purchaseSeries, ...netSalesSeries];

    return (
      <Box sx={{ mt: 2 }}>
        <LineChart
          series={allSeries}
          xAxis={[{
            scaleType: 'point',
            data: filteredLabels,
            tickLabelStyle: {
              fontSize: 12
            }
          }]}
          grid={{ 
            vertical: showGrid, 
            horizontal: showGrid 
          }}
          sx={{
            '.MuiLineElement-root': {
              strokeWidth: 3,
            },
            '.MuiAreaElement-root': {
              fillOpacity: 0.7,
            },
            '.MuiMarkElement-root': {
              stroke: 'white',
              scale: '0.6',
              fill: 'currentColor',
              strokeWidth: 2,
            },
            // Enable zoom and pan
            '& .plot-container': {
              cursor: 'crosshair',
            },
          }}
          height={chartHeight}
          margin={{ left: 80, right: 80, top: 20, bottom: 30 }}
          slotProps={{
            legend: {
              direction: 'row',
              position: {
                vertical: 'top',
                horizontal: 'right',
              },
            },
            // Enable tooltip
            tooltip: showTooltip ? {
              trigger: 'axis',
              formatter: (params) => {
                return params.map(param => 
                  `${param.seriesName}: ${param.value.toLocaleString()}`
                ).join('<br/>');
              }
            } : { trigger: 'none' },
          }}
          // Enable zoom functionality
          onAxisClick={(event, data) => {
            console.log('Axis clicked:', data);
          }}
        />
      </Box>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      aria-labelledby="yearly-sales-modal-title"
    >
        
    <DialogTitle id="yearly-sales-modal-title"  sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">
            Yearly Sales & Purchase Analysis
        </Typography>
        <Button 
            onClick={onClose} 
            color="inherit" 
            sx={{ minWidth: 'auto', p: 1 }}
        >
            <CloseIcon />
        </Button>
    </DialogTitle>    
      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={3} >
          <Grid item xs={12} width={'100%'} >
            <Paper elevation={1} sx={{ p: 2 }}  >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Monthly Net Sales vs Purchases by Year
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel id="year-select-label">Select Years</InputLabel>
                    <Select
                      labelId="year-select-label"
                      id="year-select"
                      multiple
                      value={selectedYears}
                      onChange={handleYearChange}
                      label="Select Years"
                      size="small"
                    >
                      {[...Array(5)].map((_, i) => (
                        <MenuItem key={currentYear - i} value={currentYear - i}>
                          {currentYear - i}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button 
                    variant="contained" 
                    onClick={handleApplyFilter}
                    disabled={selectedYears.length === 0}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>

              {/* Chart Controls */}
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Chart Controls
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                  {/* Grid Toggle */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showGrid}
                        onChange={(e) => setShowGrid(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        {showGrid ? <GridOnIcon fontSize="small" /> : <GridOffIcon fontSize="small" />}
                        Grid
                      </Box>
                    }
                  />

                  {/* Tooltip Toggle */}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showTooltip}
                        onChange={(e) => setShowTooltip(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Tooltips"
                  />

                  {/* Chart Height Slider */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                    <Typography variant="body2" sx={{ minWidth: 'fit-content' }}>
                      Height:
                    </Typography>
                    <Slider
                      value={chartHeight}
                      onChange={(e, value) => setChartHeight(value)}
                      min={300}
                      max={800}
                      step={50}
                      size="small"
                      valueLabelDisplay="auto"
                      sx={{ flex: 1 }}
                    />
                  </Box>

                  {/* Quick Actions */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Reset to default height">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setChartHeight(400)}
                        startIcon={<FullscreenIcon />}
                      >
                        Reset
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Analysis Tips */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    💡 Tips: Use the controls above to customize your view. 
                    Hover over data points for detailed values. Toggle grid lines for cleaner visualization.
                    Use time range controls below the chart to focus on specific periods.
                  </Typography>
                </Box>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {renderChart()}
                  
                  {/* Time Range Zoom Controls - Below Chart */}
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2 }}>
                      📅 Time Range Zoom
                    </Typography>
                    
                    {/* Preset Buttons */}
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                      {[
                        { label: 'All Year', value: 'all' },
                        { label: 'Q1', value: 'q1' },
                        { label: 'Q2', value: 'q2' },
                        { label: 'Q3', value: 'q3' },
                        { label: 'Q4', value: 'q4' },
                        { label: 'H1', value: 'h1' },
                        { label: 'H2', value: 'h2' },
                      ].map((preset) => (
                        <Button
                          key={preset.value}
                          size="small"
                          variant={selectedMonths === preset.value ? 'contained' : 'outlined'}
                          onClick={() => handleMonthPreset(preset.value)}
                          sx={{ minWidth: 'auto' }}
                        >
                          {preset.label}
                        </Button>
                      ))}
                    </Box>

                    {/* Custom Range Slider */}
                    <Box sx={{ px: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Custom Range: {data?.labels[zoomRange[0]]} - {data?.labels[zoomRange[1]]}
                      </Typography>
                      <Slider
                        value={zoomRange}
                        onChange={handleZoomChange}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => data?.labels[value] || ''}
                        min={0}
                        max={11}
                        step={1}
                        marks={data?.labels.map((label, index) => ({
                          value: index,
                          label: label.substring(0, 3) // Show abbreviated month names
                        }))}
                        sx={{
                          '& .MuiSlider-mark': {
                            backgroundColor: 'currentColor',
                          },
                          '& .MuiSlider-markLabel': {
                            fontSize: '0.75rem',
                          },
                        }}
                      />
                    </Box>
                  </Box>
                  
                  {/* Enhanced Legend with Stats */}
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Chart Legend & Quick Stats:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 20, height: 3, bgcolor: '#4caf50', borderRadius: 1 }} />
                        <Typography variant="body2">Net Sales (Line)</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 20, height: 15, bgcolor: '#c8e6c9', borderRadius: 1, opacity: 0.7 }} />
                        <Typography variant="body2">Purchase Costs (Filled Areas)</Typography>
                      </Box>
                    </Box>
                    
                    {/* Quick Statistics */}
                    {data && data.years_data && Object.keys(data.years_data).length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Quick Statistics:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {Object.keys(data.years_data).map(year => {
                            // Calculate totals based on current zoom range
                            const netSalesData = data.years_data[year].net_sales?.data.slice(zoomRange[0], zoomRange[1] + 1) || [];
                            const purchaseData = data.years_data[year].purchases?.data.slice(zoomRange[0], zoomRange[1] + 1) || [];
                            
                            const netSalesTotal = netSalesData.reduce((sum, val) => sum + val, 0);
                            const purchaseTotal = purchaseData.reduce((sum, val) => sum + val, 0);
                            const profit = netSalesTotal - purchaseTotal;
                            
                            const rangeText = zoomRange[0] === 0 && zoomRange[1] === 11 
                              ? 'Year' 
                              : `${data.labels[zoomRange[0]]}-${data.labels[zoomRange[1]]}`;
                            
                            return (
                              <Chip
                                key={year}
                                label={`${year} (${rangeText}): ${profit >= 0 ? '+' : ''}${profit.toLocaleString()}`}
                                color={profit >= 0 ? 'success' : 'error'}
                                variant="outlined"
                                size="small"
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    )}
                    
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                      This combination chart shows purchase costs as filled areas and net sales as lines on the same view, 
                      making it easy to directly compare volumes and trends. The filled areas represent purchase volumes 
                      while lines show sales performance trends. Use the controls above to customize your analysis view.
                    </Typography>
                  </Box>
                </>
              )}

                {/* Key insights section - updated for net sales vs purchases
                <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                    Key Insights:
                    </Typography>
                    <Typography variant="body2" paragraph>
                    This chart displays the monthly net sales revenue (sales minus returns) versus 
                    purchase costs for the selected years. This comparison helps identify profitability 
                    trends, seasonal patterns, and the relationship between your purchasing strategy 
                    and sales performance across different periods.
                    </Typography>
                </Box> */}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default YearlySalesModal;
